import { useState, useEffect } from 'react';
import { Bell, X, SpinnerGap, Warning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { requestNotificationPermission } from '../utils/messaging';
import { registerFcmToken, updatePreferences } from '../utils/notificationService';

const DISMISS_KEY = 'notification_optin_dismissed';
const CHAT_COUNT_KEY = 'notification_optin_chat_count';
const VALUE_TRIGGER_PREFIX = 'notification_optin_trigger_';
const NEVER_KEY = 'notification_optin_never';

/**
 * Delayed opt-in prompt shown to signed-in users after 3rd chat session.
 * "Enable" → request permission, register token, set preferences.
 * "Not now" → dismiss for 30 days.
 * "Never" → set enabled: false permanently.
 */
export default function NotificationOptIn() {
  const { currentUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkVisibility = () => {
    if (!currentUser) return false;
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') return false;
    if (Notification.permission === 'denied') return false;
    // User said "Never" — respect permanently
    if (localStorage.getItem(NEVER_KEY)) return false;

    // Check if dismissed with "Not now"
    const dismissed = localStorage.getItem(DISMISS_KEY);
    const isDismissed = dismissed && (Date.now() - parseInt(dismissed, 10) < 30 * 24 * 60 * 60 * 1000);

    // Chat count trigger (primary)
    const chatCount = parseInt(localStorage.getItem(CHAT_COUNT_KEY) || '0', 10);
    if (chatCount >= 3 && !isDismissed) return true;

    // Value-event triggers override the 30-day dismiss (but not "Never")
    if (isDismissed) {
      const events = ['first_insight', 'first_health_log', 'first_community_post', 'first_goal', 'first_cycle_log'];
      for (const evt of events) {
        const fired = localStorage.getItem(VALUE_TRIGGER_PREFIX + evt);
        const used = localStorage.getItem(VALUE_TRIGGER_PREFIX + evt + '_shown');
        if (fired && !used) {
          localStorage.setItem(VALUE_TRIGGER_PREFIX + evt + '_shown', '1');
          return true;
        }
      }
    }

    return false;
  };

  useEffect(() => {
    if (checkVisibility()) setVisible(true);
  }, [currentUser]);

  // Listen for value-trigger events fired from other components
  useEffect(() => {
    const handler = () => {
      if (checkVisibility()) setVisible(true);
    };
    window.addEventListener('notification-value-trigger', handler);
    return () => window.removeEventListener('notification-value-trigger', handler);
  }, [currentUser]);

  const handleEnable = async () => {
    setEnabling(true);
    setError(null);

    try {
      const result = await requestNotificationPermission();
      if (!result.ok) {
        const messages: Record<string, string> = {
          denied: 'Notifications are blocked in your browser settings. You can enable them there anytime.',
          vapid: 'Notification setup isn\u2019t quite ready yet. Try again in a bit.',
          token_error: 'Almost there, but setup didn\u2019t finish. Give it another try in a moment.',
        };
        setError(messages[result.reason] || messages.token_error);
        setEnabling(false);
        return;
      }

      await registerFcmToken(result.token, 'web');
      await updatePreferences({
        enabled: true,
        categories: { pattern: true, lapse: true, research: true, progress: true, insight: true, appointment: true, goal: true, cycle: true, reminder: true, checkin: true },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Only dismiss on success
      setVisible(false);
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError('That didn\u2019t quite work. You can try again or enable notifications from Settings anytime.');
      setEnabling(false);
    }
  };

  const handleNotNow = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleNever = async () => {
    try {
      await updatePreferences({ enabled: false });
    } catch {
      // Best effort
    }
    localStorage.setItem(NEVER_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md z-50 animate-in slide-in-from-bottom">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-pink-100 dark:border-pink-800 p-5">
        <button
          onClick={handleNotNow}
          disabled={enabling}
          className="absolute top-3 right-3 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center flex-shrink-0">
            <Bell size={20} className="text-white" weight="fill" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">Stay in the loop</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Get personalized health nudges from Romy — no more than a couple per week.
            </p>

            {error && (
              <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <Warning size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 dark:text-amber-200">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleEnable}
                disabled={enabling}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2"
              >
                {enabling && <SpinnerGap size={14} className="animate-spin" />}
                {enabling ? 'Enabling...' : 'Enable'}
              </button>
              <button
                onClick={handleNotNow}
                disabled={enabling}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 transition-colors disabled:opacity-50"
              >
                Not now
              </button>
              <button
                onClick={handleNever}
                disabled={enabling}
                className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
              >
                Never
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Increment the chat count for opt-in trigger tracking.
 * Call this after a successful chat message.
 */
export function incrementChatCount(): void {
  const current = parseInt(localStorage.getItem(CHAT_COUNT_KEY) || '0', 10);
  localStorage.setItem(CHAT_COUNT_KEY, String(current + 1));
}

/**
 * Fire a value-trigger event to re-show the notification opt-in.
 * Only fires once per event type. Call from pages that deliver value:
 * - 'first_insight' — first health insight viewed
 * - 'first_health_log' — first health log created
 * - 'first_community_post' — first community post created
 */
export function fireNotificationValueTrigger(event: string): void {
  const key = VALUE_TRIGGER_PREFIX + event;
  if (localStorage.getItem(key)) return; // Already fired
  localStorage.setItem(key, '1');
  window.dispatchEvent(new CustomEvent('notification-value-trigger'));
}
